/**
 * O que o cliente deixou aguardando a equipe (E149.F2 — GUS-149, ADR-0102 §3): o questionamento,
 * que a equipe responde ou marca com "nova versão em preparação", e o apontamento de campo, que a
 * equipe resolve dizendo o que foi feito. "Nova versão" é registro: não reemite nem altera o laudo
 * entregue (a versão assinada nova é da GUS-113/145).
 */
import { useState } from 'react';

import { Seg } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type {
  MensagemData,
  MensagemEntradaData,
  PendenciasDaConversaData,
  RecusaDaConversa,
} from '@/lib/conversa_do_caso';

import { Compositor } from './Compositor';

const t = ptBR.conversaDoCaso;

type Enviar = (entrada: MensagemEntradaData) => Promise<RecusaDaConversa | null>;
type Desfecho = 'respondido' | 'nova_versao';

function RespostaAoQuestionamento({ item, enviar }: { item: MensagemData; enviar: Enviar }) {
  const [desfecho, setDesfecho] = useState<Desfecho>('respondido');
  const responder = (texto: string | null) =>
    enviar({ tipo: 'resposta_ao_questionamento', responde_a: item.id, desfecho, texto });
  return (
    <div className="space-y-3">
      <Seg
        label={t.equipe.responder}
        value={desfecho}
        onChange={setDesfecho}
        options={[
          { value: 'respondido', label: t.equipe.responder, testId: `responder-${item.id}` },
          { value: 'nova_versao', label: t.equipe.novaVersao, testId: `nova-versao-${item.id}` },
        ]}
      />
      {desfecho === 'respondido' ? (
        <Compositor
          key="respondido"
          rotulo={t.equipe.responderRotulo}
          obrigatorio
          testId={`resposta-${item.id}`}
          onEnviar={responder}
        />
      ) : (
        <Compositor
          key="nova_versao"
          rotulo={t.equipe.responderRotulo}
          obrigatorio={false}
          nota={t.equipe.novaVersaoNota}
          botao={t.equipe.novaVersao}
          testId={`resposta-${item.id}`}
          onEnviar={responder}
        />
      )}
    </div>
  );
}

/** A ação da equipe num item aberto da lista: só o que cabe a ela responder. */
export function acaoDaEquipe(enviar: Enviar) {
  function Acao(item: MensagemData) {
    if (item.tipo === 'questionamento') {
      return <RespostaAoQuestionamento item={item} enviar={enviar} />;
    }
    if (item.tipo === 'apontamento') {
      return (
        <Compositor
          rotulo={t.equipe.resolucaoRotulo}
          obrigatorio
          botao={t.equipe.resolver}
          testId={`resposta-${item.id}`}
          onEnviar={(texto) =>
            enviar({
              tipo: 'resposta_ao_apontamento',
              responde_a: item.id,
              desfecho: 'resolvido',
              texto,
            })
          }
        />
      );
    }
    return null;
  }
  return Acao;
}

export interface PendentesDaEquipeProps {
  pendencias: PendenciasDaConversaData;
}

/** O resumo do que segue aberto: o que aguarda a equipe e o que aguarda o cliente. */
export function PendentesDaEquipe({ pendencias }: PendentesDaEquipeProps) {
  const daEquipe = pendencias.questionamentos_abertos + pendencias.apontamentos_abertos;
  return (
    <section className="border border-border bg-surface p-4" data-testid="conversa-pendentes">
      <h2 className="text-caption font-semibold uppercase tracking-[0.14em] text-fg-dim">
        {t.equipe.pendentesTitulo}
      </h2>
      <p className="mt-1 text-body-sm text-fg">
        {daEquipe === 0
          ? t.equipe.nadaPendente
          : t.equipe.abertos
              .replace('{{q}}', String(pendencias.questionamentos_abertos))
              .replace('{{a}}', String(pendencias.apontamentos_abertos))}
      </p>
      {pendencias.pedidos_abertos > 0 ? (
        <p className="mt-1 text-caption text-fg-muted">
          {t.equipe.aguardandoCliente.replace('{{n}}', String(pendencias.pedidos_abertos))}
        </p>
      ) : null}
    </section>
  );
}
